﻿import * as React from "react";
import { Dictionary, IKeyValuePair, WatcherComponent } from "../../Utilities";
import Draggable, { DraggableData } from "react-draggable";
import { PointerEventsManager } from "../Managers";
import { PointerEventsProperty } from "csstype";

export interface ISubWindowProps {
    closeWindow: (moduleID: number) => void;
    content: ISubWindowContent;
}

export interface ISubWindowContent {
    body: React.ReactNode;
    horizontalPos?: "Left" | "Center" | "Right";
    isDominant?: boolean;
    moduleID: number;
    overrideClose?: boolean;
    subWindowKey: string;
    title?: string;
    verticalPos?: "Top" | "Center" | "Bottom";
}

export interface ISubWindowState {
    isTop: boolean;
    location: {
        x: number;
        y: number;
    };
    pos: number;
}

export class SubWindow extends React.Component<
    ISubWindowProps,
    ISubWindowState
> {
    private static _initialise(): void {
        if (!SubWindow._isInitialised) {
            SubWindow._heirarchy = new WatcherComponent();
            SubWindow._heirarchy.value = new Dictionary();
            SubWindow._isInitialised = true;
        }
    }

    private static _sortHeirarchy(): void {
        SubWindow._heirarchy.value.sort(
            (
                a: IKeyValuePair<string, Date>,
                b: IKeyValuePair<string, Date>,
            ): number => a.value.valueOf() - b.value.valueOf(),
        );
    }

    constructor(props: ISubWindowProps) {
        super(props);

        SubWindow._initialise();

        // Register watchers.
        SubWindow._heirarchy.addOnSetWatcher(this._hierarchyWatcher);
        SubWindow._heirarchy.value.push(
            this.props.content.subWindowKey,
            new Date(Date.now()),
        );
        SubWindow._sortHeirarchy();
        const heirarchyPos: number = SubWindow._heirarchy.value.getIndexOf(
            this.props.content.subWindowKey,
        );

        this.state = {
            isTop: heirarchyPos === SubWindow._heirarchy.value.length - 1,
            location: {
                x: 0,
                y: 0,
            },
            pos: heirarchyPos,
        };
    }

    componentDidMount(): void {
        window.addEventListener(`resize`, this._onResize);
    }

    componentWillUnmount(): void {
        SubWindow._heirarchy.value.remove(this.props.content.subWindowKey);
        window.removeEventListener(`resize`, this._onResize);
    }

    private _dragRef: HTMLElement;

    private static _isInitialised = false;

    private static _heirarchy: WatcherComponent<Dictionary<string, Date>>;

    public close = (): void => {
        this.props.closeWindow(this.props.content.moduleID);
    };

    private readonly _mouseDown = (): void => {
        SubWindow._heirarchy.value.set(
            this.props.content.subWindowKey,
            new Date(Date.now()),
        );
        SubWindow._sortHeirarchy();
        SubWindow._heirarchy.triggerWatchers();
    };

    private readonly _startDrag = (): void => {
        if (!this.props.content.isDominant) {
            PointerEventsManager.togglePointerEvents(false);
        }
    };

    private readonly _endDrag = (): void => {
        if (!this.props.content.isDominant) {
            PointerEventsManager.togglePointerEvents(true);
        }
    };

    private readonly _hierarchyWatcher = (): void => {
        const heirarchyPos: number = SubWindow._heirarchy.value.getIndexOf(
            this.props.content.subWindowKey,
        );

        this.setState({
            isTop: heirarchyPos === SubWindow._heirarchy.value.length - 1,
            pos: heirarchyPos,
        });
    };

    private readonly _onDrag = (e: MouseEvent, ui: DraggableData): void => {
        this.setState({
            location: {
                x: ui.x,
                y: ui.y,
            },
        });
    };

    private readonly _onResize = (): void => {
        const bounds: ClientRect = this._dragRef.getBoundingClientRect();

        this.setState((prevState) => {
            // On resize check if the subwindow is out of the body of the document.
            // Check the X axis.
            let x: number = prevState.location.x;

            switch (this.props.content.horizontalPos) {
                case `Left`:
                    if (bounds.right > window.innerWidth) {
                        x = window.innerWidth - bounds.width;
                    }
                    break;
                case undefined:
                case `Center`:
                    if (bounds.right > window.innerWidth) {
                        x = (window.innerWidth - bounds.width) / 2;
                    } else if (bounds.left < 0) {
                        x = -1 * ((window.innerWidth - bounds.width) / 2);
                    }

                    break;
                case `Right`:
                    if (bounds.left < 0) {
                        x = -1 * (window.innerWidth - bounds.width);
                    }
                    break;
                default:
                    break;
            }

            // Then check the Y axis.
            let y: number = prevState.location.y;

            switch (this.props.content.verticalPos) {
                case `Top`:
                    if (bounds.bottom > window.innerHeight) {
                        y = window.innerHeight - bounds.height;
                    }
                    break;
                case undefined:
                case `Center`:
                    if (bounds.bottom > window.innerWidth) {
                        y = (window.innerHeight - bounds.height) / 2;
                    } else if (bounds.top < 0) {
                        y = -1 * ((window.innerHeight - bounds.height) / 2);
                    }
                    break;
                case `Bottom`:
                    if (bounds.top < 0) {
                        y = -1 * (window.innerHeight - bounds.height);
                    }
                    break;
                default:
                    break;
            }

            return {
                location: {
                    x,
                    y,
                },
            };
        });
    };

    render(): React.ReactElement {
        let dockClass = `root-windowdock`,
            handle = `.subwindow-head`;

        // If the subWindow "is dominant" then disable dragging and cover the rest of SmartTools.
        if (this.props.content.isDominant) {
            handle = `nothing`;
            dockClass = `root-windowdock dominant`;
        }

        // If the sub window is "on top" set the relevant shadow effect.
        let containerClass = `clickeventcontainer-subwindow`;

        if (this.state.isTop) {
            containerClass = `clickeventcontainer-subwindow top`;
        }

        let pointerEventsProperty: PointerEventsProperty = `none`;

        if (this.state.isTop) {
            pointerEventsProperty = `auto`;
        }

        return (
            <div
                className={`${dockClass} ${this.props.content.horizontalPos}`}
                style={{
                    zIndex: 1000 + this.state.pos,
                }}
            >
                <div
                    className={`windowdock-subdock ${this.props.content.verticalPos}`}
                >
                    <Draggable
                        bounds={`body`}
                        handle={handle}
                        onDrag={this._onDrag}
                        onMouseDown={this._mouseDown}
                        onStart={this._startDrag}
                        onStop={this._endDrag}
                        position={this.state.location}
                    >
                        <div
                            className={containerClass}
                            ref={(r): HTMLElement => (this._dragRef = r)}
                        >
                            <div className={`subwindow-head`}>
                                {this.props.content.title && (
                                    <div className={`head-titlecontainer`}>
                                        {this.props.content.title}
                                    </div>
                                )}
                                {!this.props.content.overrideClose && (
                                    <div className={`head-closecontainer`}>
                                        <div
                                            className={`closecontainer-closebutton`}
                                            onClick={this.close}
                                        >
                                            <img
                                                src={`/Images/CloseIcon.svg`}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div
                                className={`subwindow-bodycover`}
                                style={{ pointerEvents: pointerEventsProperty }}
                            >
                                <div className={`subwindow-body`}>
                                    {this.props.content.body}
                                </div>
                            </div>
                        </div>
                    </Draggable>
                </div>
            </div>
        );
    }
}
